import {
  VERSAO_ATUAL_POLITICA_COOKIES,
  consentimentoCookiesLocalSchema,
  type CategoriasConsentimentoCookies,
  type ConsentimentoCookiesLocal,
  type OrigemConsentimentoCookies,
} from '@erplight/shared';
import { atualizarCookiesRum } from '@/lib/rum';
import apiService from '@/services/api';

export type {
  CategoriasConsentimentoCookies,
  ConsentimentoCookiesLocal,
  OrigemConsentimentoCookies,
};

export const ERPLIGHT_COOKIE_CONSENT_KEY = 'ERPLIGHT_COOKIE_CONSENT';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * Lê e valida o consentimento armazenado no dispositivo.
 * Se inexistente, inválido ou se a versão da política divergir da versão atual (#378),
 * retorna null para que a interface reexiba a solicitação de consentimento.
 */
export const obterConsentimentoLocal = (): ConsentimentoCookiesLocal | null => {
  try {
    const raw = localStorage.getItem(ERPLIGHT_COOKIE_CONSENT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const resultado = consentimentoCookiesLocalSchema.safeParse(parsed);
    if (!resultado.success) {
      return null;
    }

    if (resultado.data.versaoPolitica !== VERSAO_ATUAL_POLITICA_COOKIES) {
      return null;
    }

    return resultado.data;
  } catch {
    return null;
  }
};

/**
 * Aplica dinamicamente as permissões nos SDKs terceiros no navegador:
 * 1. Google Consent Mode v2 (analytics_storage, ad_storage, ad_user_data, ad_personalization)
 * 2. Meta Pixel (fbq('consent', 'grant' | 'revoke'))
 * 3. CloudWatch RUM (allowCookies & clearCookies)
 */
export const aplicarConsentimento = (categorias: CategoriasConsentimentoCookies): void => {
  // 1. Google Consent Mode v2
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('consent', 'update', {
        analytics_storage: categorias.medicao ? 'granted' : 'denied',
        ad_storage: categorias.publicidade ? 'granted' : 'denied',
        ad_user_data: categorias.publicidade ? 'granted' : 'denied',
        ad_personalization: categorias.publicidade ? 'granted' : 'denied',
      });
    } catch (e) {
      console.warn('Erro ao atualizar Google Consent Mode:', e);
    }
  }

  // 2. Meta Pixel
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    try {
      if (categorias.publicidade) {
        window.fbq('consent', 'grant');
      } else {
        window.fbq('consent', 'revoke');
      }
    } catch (e) {
      console.warn('Erro ao atualizar Meta Pixel consent:', e);
    }
  }

  // 3. CloudWatch RUM
  atualizarCookiesRum(categorias.medicao);
};

/**
 * Salva a escolha do usuário no dispositivo, aplica aos trackers e
 * opcionalmente sincroniza com o servidor (se autenticado) para prova de auditoria LGPD art. 8º §2º.
 */
export const salvarConsentimento = async (
  categorias: CategoriasConsentimentoCookies,
  origem: OrigemConsentimentoCookies
): Promise<ConsentimentoCookiesLocal> => {
  const registro: ConsentimentoCookiesLocal = {
    versaoPolitica: VERSAO_ATUAL_POLITICA_COOKIES,
    categorias,
    data: new Date().toISOString(),
    origem,
  };

  try {
    localStorage.setItem(ERPLIGHT_COOKIE_CONSENT_KEY, JSON.stringify(registro));
  } catch (e) {
    console.warn('Não foi possível gravar o consentimento no localStorage:', e);
  }

  // Aplica aos rastreadores imediatamente
  aplicarConsentimento(categorias);

  // Notifica componentes na mesma janela
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('erplight:consent-changed', { detail: registro }));
  }

  // Se logado, envia para a API para registro legal
  try {
    await apiService.membros.salvarConsentimento({
      versaoPolitica: registro.versaoPolitica,
      categorias: registro.categorias,
      origem: registro.origem,
    });
  } catch {
    // Falhas de rede ou usuário anônimo não impedem a persistência local
  }

  return registro;
};

/**
 * Dispara evento global para abrir o modal de gerenciamento de preferências de cookies (<= 2 cliques).
 */
export const abrirPreferenciasCookies = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('erplight:abrir-cookies'));
  }
};
